import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import TableLoader from "../Loaders/TableLoader";
import SubmissionEvidenceLinks from "./SubmissionEvidenceLinks";

function statusChipProps(status) {
  const s = String(status || "").trim();
  if (s === "Approved") return { label: s, sx: { bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 600, border: "1px solid #A5D6A7" } };
  if (s === "Submitted") return { label: s, sx: { bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 600, border: "1px solid #90CAF9" } };
  if (s === "InReview") return { label: s, sx: { bgcolor: "#FFF8E1", color: "#E65100", fontWeight: 600, border: "1px solid #FFE082" } };
  if (s === "Rejected") return { label: s, sx: { bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 600, border: "1px solid #FFCDD2" } };
  return { label: s || "—", sx: { fontWeight: 600 } };
}

function fmtDate(v) {
  if (v == null || v === "") return "—";
  const m = moment(v);
  return m.isValid() ? m.format("DD-MM-YYYY HH:mm") : String(v);
}

function trunc(s, n) {
  if (s == null) return "—";
  const t = String(s).trim();
  if (!t) return "—";
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

/**
 * My Points — submissions for one activity (FY) in a compact grid.
 */
export default function ActivitySubmissionDrilldown({ activityName, submissions, loading }) {
  const sorted = useMemo(() => {
    if (!Array.isArray(submissions)) return [];
    return [...submissions].sort((a, b) => {
      const ida = Number(a.submissionId ?? a.SubmissionId) || 0;
      const idb = Number(b.submissionId ?? b.SubmissionId) || 0;
      return idb - ida;
    });
  }, [submissions]);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 0.5,
        mb: 1,
        ml: 0,
        mr: 0,
        border: "1px solid #e9d5ff",
        borderRadius: 2,
        bgcolor: "#faf5ff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #ede9fe", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#4c1d95" }}>
          Submissions
        </Typography>
        <Typography variant="caption" sx={{ color: "#6d28d9" }}>
          {activityName}
        </Typography>
        <Typography variant="caption" sx={{ color: "#9ca3af" }}>
          ({sorted.length} record{sorted.length === 1 ? "" : "s"})
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 280 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["ID", "Status", "Title", "Notes", "Evidence", "Req. pts", "Appr. pts", "Created", "Submitted", "Reviewed"].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "#5b21b6",
                    bgcolor: "#f3e8ff",
                    whiteSpace: "nowrap",
                    py: 1,
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ border: 0, py: 2 }}>
                  <TableLoader />
                </TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ color: "#6b7280", fontStyle: "italic", py: 2 }}>
                  No submissions for this activity in the selected points year.
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((r) => {
                const id = r.submissionId ?? r.SubmissionId;
                const st = r.status ?? r.Status;
                const chip = statusChipProps(st);
                return (
                  <TableRow key={String(id)} hover sx={{ "& td": { borderColor: "#f3e8ff", fontSize: "0.8125rem" } }}>
                    <TableCell sx={{ fontWeight: 600, color: "#4c1d95" }}>{id}</TableCell>
                    <TableCell>
                      <Chip size="small" label={chip.label} sx={chip.sx} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {trunc(r.title ?? r.Title, 80)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {trunc(r.description ?? r.Description, 100)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180, verticalAlign: "top" }}>
                      <SubmissionEvidenceLinks submission={r} compact />
                    </TableCell>
                    <TableCell>{r.requestedPoints ?? r.RequestedPoints ?? "—"}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.approvedPoints ?? r.ApprovedPoints ?? "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", color: "#4b5563" }}>{fmtDate(r.createdUtc ?? r.CreatedUtc)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", color: "#4b5563" }}>{fmtDate(r.submittedUtc ?? r.SubmittedUtc)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", color: "#4b5563" }}>{fmtDate(r.reviewedUtc ?? r.ReviewedUtc)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
